import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";

import { useTournamentCreation } from "../../context/TournamentCreationContext";
import { DASHBOARD_STACK } from "../../utils/screenNames";
import SPORT_TYPES from "../../utils/sportTypes";

const CompetitionConfigurationRefactored = () => {
  const navigate = useNavigate();
  const { formData, updateField } = useTournamentCreation();

  // Guard: redirect to step 1 if required prior-step data is missing (e.g. user refreshed)
  useEffect(() => {
    if (!formData.name || !formData.startDate || !formData.endDate) {
      navigate(DASHBOARD_STACK.COMPETITIONS, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Local UI state
  const [extraSport, setExtraSport] = useState("");
  const [categoriesName, setCategoriesName] = useState("");
  const [isSportDropdownOpen, setIsSportDropdownOpen] = useState(false);
  const [sportSearch, setSportSearch] = useState("");
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const sportTriggerRef = useRef(null);
  const sportDropdownRef = useRef(null);

  const updateDropdownPosition = () => {
    if (!sportTriggerRef.current) return;
    const rect = sportTriggerRef.current.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  };

  // Close sport dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        sportTriggerRef.current && !sportTriggerRef.current.contains(e.target) &&
        sportDropdownRef.current && !sportDropdownRef.current.contains(e.target)
      ) {
        setIsSportDropdownOpen(false);
        setSportSearch("");
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Update portal position on scroll/resize
  useEffect(() => {
    if (!isSportDropdownOpen) return;
    updateDropdownPosition();
    window.addEventListener("scroll", updateDropdownPosition, true);
    window.addEventListener("resize", updateDropdownPosition);
    return () => {
      window.removeEventListener("scroll", updateDropdownPosition, true);
      window.removeEventListener("resize", updateDropdownPosition);
    };
  }, [isSportDropdownOpen]);

  // Derive from context
  const selectedSport = formData.sport;
  const categoriesList = formData.categories;

  // --- Data ---
  const primarySportIds = [SPORT_TYPES.FOOTBALL, SPORT_TYPES.RUGBY, SPORT_TYPES.TOUCH_RUGBY, SPORT_TYPES.UNIVERSAL];
  const sports = [
    { id: SPORT_TYPES.FOOTBALL, name: "Football" },
    { id: SPORT_TYPES.RUGBY, name: "Rugby" },
    { id: SPORT_TYPES.TOUCH_RUGBY, name: "Touch Rugby" },
    { id: "other", name: "Other" },
  ];
  const otherSports = Object.values(SPORT_TYPES)
    .filter((v) => !primarySportIds.includes(v))
    .map((v) => ({ id: v, name: v.charAt(0).toUpperCase() + v.slice(1) }));
  const filteredOtherSports = otherSports.filter((s) =>
    s.name.toLowerCase().includes(sportSearch.toLowerCase())
  );

  // --- Handlers ---

  const handleAddCategory = () => {
    if (
      categoriesName.trim() !== "" &&
      !categoriesList.includes(categoriesName.trim())
    ) {
      updateField("categories", [...categoriesList, categoriesName.trim()]);
      setCategoriesName("");
    }
  };

  const handleRemoveCategory = (category) => {
    updateField("categories", categoriesList.filter((c) => c !== category));
  };

  const handleNext = () => {
    if (!selectedSport) return toast.error("Please select a sport");

    if (categoriesList.length === 0) {
      updateField("categories", [formData.name.trim()]);
    }

    navigate(DASHBOARD_STACK.COMPETITIONS_VENUE);
  };

  return (
    <div className="h-full bg-[#000F1F] text-[#FEFCEB] flex flex-1 flex-col items-center overflow-hidden">
      <div className="w-full max-w-2xl flex-1 flex flex-col items-center py-6">
        {/* Header */}
        <div className="flex flex-col gap-[16px] items-center justify-center shrink-0">
          <h1 className="text-[24px] sm:text-[32px] md:text-[40px] font-medium text-[#FEFCEB] text-center tracking-[1px]">Create new competition</h1>
          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-3 sm:gap-[28px]">
            <div className="flex items-center gap-[8px]">
              <div className="w-5 h-5 rounded-full bg-[#00E7FF] flex items-center justify-center relative">
                <svg className="w-[10px] h-[10px]" viewBox="0 0 10 8" fill="none">
                  <path d="M1 4L3.5 6.5L9 1" stroke="#000F1F" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <span className="hidden sm:inline text-[14px] font-medium text-[#E5E5D6]">Competition details</span>
            </div>
            <div className="flex items-center gap-[8px]">
              <div className="w-5 h-5 rounded-full bg-[#00E7FF] text-[#000F1F] flex items-center justify-center font-semibold text-[14px]">2</div>
              <span className="hidden sm:inline text-[14px] font-medium text-[#FEFCEB]">Configuration</span>
            </div>
            <div className="flex items-center gap-[8px]">
              <div className="w-5 h-5 rounded-full bg-[#192634] text-[#7F8585] flex items-center justify-center font-semibold text-[14px]">3</div>
              <span className="hidden sm:inline text-[14px] font-medium text-[#7F8585]">Venue details</span>
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="w-full max-w-[480px] flex-1 my-5 mx-3 sm:mx-0 border border-app-border rounded-2xl p-4 sm:p-6 overflow-y-auto flex flex-col gap-[16px]">

          {/* Sport Selection */}
          <div className="flex flex-col gap-[12px]">
            <label className="block text-[14px] font-normal text-clubbie-white ">Select sport</label>
            <div className="flex flex-col gap-[12px]">
              <div className="flex flex-col gap-[12px]">
                {sports.filter(s => s.id !== 'other').map((sport) => (
                  <label key={sport.id} className="flex items-center gap-[12px] cursor-pointer">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center relative ${selectedSport === sport.id ? 'border-[#00E7FF]' : 'border-[#333E48]'}`}>
                      {selectedSport === sport.id && (
                        <div className="w-2.5 h-2.5 rounded-full bg-[#00E7FF]" />
                      )}
                    </div>
                    <input
                      type="radio"
                      className="hidden"
                      checked={selectedSport === sport.id}
                      onChange={() => {
                        updateField("sport", sport.id);
                        setExtraSport('');
                      }}
                    />
                    <span className="text-sm text-[#FEFCEB]">{sport.name}</span>
                  </label>
                ))}
              </div>
              {sports.find(s => s.id === 'other') && (
                <label className="flex items-center gap-[12px] cursor-pointer">
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center relative ${selectedSport === 'other' || extraSport ? 'border-[#00E7FF]' : 'border-[#333E48]'}`}>
                    {(selectedSport === 'other' || extraSport) && (
                      <div className="w-2.5 h-2.5 rounded-full bg-[#00E7FF]" />
                    )}
                  </div>
                  <input
                    type="radio"
                    className="hidden"
                    checked={selectedSport === 'other' || !!extraSport}
                    onChange={() => {
                      updateField("sport", "other");
                    }}
                  />

                  <div className="flex items-center gap-[12px]">
                    <span className="text-sm text-[#FEFCEB]">Other</span>

                    {(selectedSport === 'other' || extraSport) && (
                      <div className="w-[320px]" ref={sportTriggerRef}>
                        <div
                          className={`flex items-center justify-between h-11 px-4 bg-[#021529] border rounded-full cursor-pointer transition-colors ${isSportDropdownOpen ? "border-[#00E7FF]" : "border-transparent"}`}
                          onClick={() => setIsSportDropdownOpen(!isSportDropdownOpen)}
                        >
                          <span className={`text-[15px] ${extraSport ? "text-[#FEFCEB]" : "text-[#7F8585]"}`}>
                            {extraSport ? extraSport.charAt(0).toUpperCase() + extraSport.slice(1) : "Select a sport..."}
                          </span>
                          <svg className={`w-4 h-4 text-[#FEFCEB] transition-transform ${isSportDropdownOpen ? "rotate-180" : ""}`} viewBox="0 0 16 16" fill="none">
                            <path d="M4 6l4 4 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>

                        {isSportDropdownOpen && createPortal(
                          <div
                            ref={sportDropdownRef}
                            className="fixed bg-[#000F1F] border border-[#192634] rounded-[20px] shadow-lg overflow-hidden z-[1000]"
                            style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
                          >
                            <div className="relative">
                              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
                                <Search size={16} />
                              </div>
                              <input
                                type="text"
                                className="w-full h-10 bg-transparent text-[#FEFCEB] text-sm outline-none placeholder-[#7F8585] pl-9 pr-4"
                                placeholder="Search"
                                value={sportSearch}
                                onChange={(e) => setSportSearch(e.target.value)}
                                autoFocus
                              />
                            </div>
                            <div className="max-h-[200px] overflow-y-auto border-t border-[#192634]">
                              {filteredOtherSports.length > 0 ? (
                                filteredOtherSports.map((s) => (
                                  <div
                                    key={s.id}
                                    className={`flex items-center gap-3 px-3 py-3 cursor-pointer transition-colors border-b border-[#192634] last:border-b-0 hover:bg-app-hover ${
                                      extraSport === s.id ? "bg-app-hover" : ""
                                    }`}
                                    onClick={() => {
                                      setExtraSport(s.id);
                                      updateField("sport", s.id);
                                      setIsSportDropdownOpen(false);
                                      setSportSearch("");
                                    }}
                                  >
                                    <span className={`text-sm font-medium ${extraSport === s.id ? "text-[#00E7FF]" : "text-[#FEFCEB]"}`}>{s.name}</span>
                                  </div>
                                ))
                              ) : (
                                <div className="px-3 py-3 text-[#7F8585] text-sm">No sports found</div>
                              )}
                            </div>
                          </div>,
                          document.body
                        )}
                      </div>
                    )}
                  </div>
                </label>
              )}
            </div>
          </div>

          {/* Categories */}
          <div className="flex flex-col gap-[12px]">
            <label className="block text-[14px] font-normal text-clubbie-white ">{"Competition categories (e.g. U12, U14, Senior)"}</label>
            <div className="flex flex-col gap-[8px]">
              <div className="relative w-[320px]">
                <input
                  type="text"
                  value={categoriesName}
                  onChange={(e) => setCategoriesName(e.target.value)}
                  placeholder="Add a category and click add"
                  className="w-full bg-app-surface border border-app-surface rounded-3xl px-4 py-2.5 text-sm text-[#FEFCEB] placeholder-app-text-secondary focus:border-blue-shade-2 outline-none transition-colors pr-[60px]"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddCategory()}
                />
                <button
                  onClick={handleAddCategory}
                  className="absolute right-[16px] top-1/2 -translate-y-1/2 text-app-text-secondary text-sm font-medium underline cursor-pointer hover:text-[#FEFCEB] transition-colors"
                >
                  Add
                </button>
              </div>
              {categoriesList.length > 0 && (
                <div className="flex flex-wrap gap-[8px]">
                  {categoriesList.map((cat, idx) => (
                    <div key={idx} className="bg-[#082441] px-[8px] py-[4px] rounded-full flex items-center gap-[4px]">
                      <span className="text-app-text-secondary text-sm">{cat}</span>
                      <button
                        onClick={() => handleRemoveCategory(cat)}
                        className="w-4 h-4 flex items-center justify-center text-app-text-secondary hover:text-[#FEFCEB] transition-colors"
                      >
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M9 3L3 9M3 3l6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <div className="flex items-start gap-[8px] mt-[8px]">
                <div className="w-4 h-4 flex items-center justify-center pt-[2px]">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <circle cx="8" cy="8" r="7" stroke="#5FC4EE" strokeWidth="1.5"/>
                    <path d="M8 5V8M8 11H8.01" stroke="#5FC4EE" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </div>
                <p className="text-[#7F8585] text-xs font-normal leading-[1.3]">
                  If this field is left blank, the category will be created automatically from competition name
                </p>
              </div>
            </div>
          </div>

        </div>

        {/* Buttons */}
        <div className="w-full max-w-[480px] flex flex-col gap-[12px] items-center justify-center shrink-0 mb-4">
          {/* Continue Button */}
          <button
            onClick={handleNext}
            disabled={!selectedSport}
            className={`w-full h-[44px] px-[16px] py-[8px] rounded-full text-sm font-medium transition-all ${
              !selectedSport
                ? "bg-[#002F41] text-[#021529] cursor-not-allowed"
                : "bg-[#00E7FF] text-[#021529] hover:bg-[#00cce6]"
            }`}
          >
            Continue
          </button>

          {/* Back Link */}
          <button
            onClick={() => navigate(DASHBOARD_STACK.COMPETITIONS)}
            className="h-[32px] px-[8px] py-0 text-app-text-secondary text-sm font-medium rounded-lg hover:text-[#FEFCEB] transition-colors"
          >
            Back to competition details
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompetitionConfigurationRefactored;
